import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { formatAZN } from "../lib/format";
import { SmallCaps, BrassRule, PillCTA } from "../components/atelier/primitives";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useI18n } from "../lib/i18n";

/* Kapital Bank ?order= ilə qayıdır (isCard=false), ödənilmiş sifariş də bu
   qoldan keçir — «paid» hər iki axında birinci yoxlanır ki, uğurlu ödəniş
   «gözləyirik» mətni ilə qarşılanmasın. Mətnlər t() üzərindəndir: AZ sayt
   heç bir ingiliscə sətir görməməlidir. */
function getStateBadge(t, isCard, paid) {
  if (paid) return t("success.badge_confirmed", "Təsdiqləndi");
  if (!isCard) return t("success.badge_placed", "Sifariş qəbul edildi");
  return t("success.badge_verifying", "Yoxlanılır");
}

function getHeadline(t, isCard, paid) {
  if (paid) return t("success.headline_paid", "Sifarişiniz qəbul edildi — təşəkkürlər.");
  if (!isCard) return t("success.headline_placed", "Təşəkkürlər — atelye sizinlə əlaqə saxlayacaq.");
  return t("success.headline_verifying", "Ödənişiniz təsdiqlənir…");
}

function getSubcopy(t, isCard, paid) {
  if (paid) {
    return t("success.subcopy_paid", "Stilistimiz bir iş günü ərzində sizinlə əlaqə saxlayacaq.");
  }
  if (!isCard) {
    return t("success.subcopy_placed", "Stilistimiz çatdırılmanı dəqiqləşdirmək üçün bir iş günü ərzində sizinlə əlaqə saxlayacaq.");
  }
  return t("success.subcopy_verifying", "Ödəniş təsdiqlənənə qədər bu səhifədə qalın — adətən bir neçə saniyə çəkir.");
}

export function CheckoutSuccess() {
  const { t } = useI18n();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  // Kapital Bank's return route redirects with ?order=; Stripe/COD use
  // ?order_id=. Read either so a KB shopper lands on a page that knows
  // which order they were paying for.
  const orderId = sp.get("order_id") || sp.get("order");
  const email = sp.get("email");
  const phone = sp.get("phone");
  const [state, setState] = useState({ status: "pending", payment_status: "initiated", order_id: null });
  // Set by the checkout when the shopper accepted the Falcon Society offer.
  const account = useLocation().state?.account || null;
  const [order, setOrder] = useState(null);
  const [attempt, setAttempt] = useState(0);

  // Path A: Stripe polling
  useEffect(() => {
    if (!sessionId) return;
    if (state.payment_status === "paid" || attempt >= 6) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/payments/stripe/status/${sessionId}`);
        setState(r.data);
        setAttempt(a => a + 1);
      } catch (err) {
        console.warn("[atelier] stripe poll", attempt, "failed", err?.response?.status);
        setAttempt(a => a + 1);
      }
    }, attempt === 0 ? 200 : 2000);
    return () => clearTimeout(t);
  }, [sessionId, attempt, state.payment_status]);

  // Path B: guest COD / bank transfer confirmation (contact-gated public lookup)
  useEffect(() => {
    if (sessionId) return;
    if (!orderId || (!email && !phone)) return;
    api.get(`/orders/lookup`, { params: { order_id: orderId, email, phone } })
      .then(r => setOrder(r.data))
      .catch(() => setOrder(null));
  }, [orderId, email, phone, sessionId]);

  const finalOrderId = state.order_id || order?.id || orderId;
  const trackHref = email
    ? `/orders/${finalOrderId}?email=${encodeURIComponent(email)}`
    : phone
      ? `/orders/${finalOrderId}?phone=${encodeURIComponent(phone)}`
    : `/orders/${finalOrderId}`;
  const paid = state.payment_status === "paid" || (order && order.payment_status === "paid");
  const isCard = !!sessionId;

  return (
    <div className="bg-ivory text-graphite" data-testid="checkout-success">
      <Header />
      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 lg:col-start-3 text-center">
          <SmallCaps>{getStateBadge(t, isCard, paid)}</SmallCaps>
          <h1 className="font-serif italic text-5xl lg:text-7xl mt-6 leading-[1.02]">
            {getHeadline(t, isCard, paid)}
          </h1>
          <p className="mt-8 text-graphite/75 font-light max-w-xl mx-auto">
            {getSubcopy(t, isCard, paid)}
          </p>
          {/* Close the loop the checkbox opened: say the link is coming, so
              nobody is left waiting for a password that never arrives. */}
          {account?.activation_sent && (
            <p className="mt-4 text-graphite/75 font-light max-w-xl mx-auto" data-testid="account-outcome">
              {account.outcome === "existing"
                ? t("success.account_existing", "Sifarişiniz mövcud hesabınıza əlavə edildi — giriş linki e-mailinizə göndərildi.")
                : t("success.account_created", "Hesabınız hazırlanır — parol yaratmaq üçün e-mailinizə link göndərdik.")}
            </p>
          )}
          {order && (
            <div className="mt-10 inline-block border border-espresso/40 bg-stone/20 px-8 py-6 text-left" data-testid="guest-order-summary">
              <SmallCaps>{t("order.label", "Sifariş")}</SmallCaps>
              <div className="font-serif italic text-3xl mt-2">#{order.display_id}</div>
              <div className="mt-2 text-graphite/75 text-sm">
                {order.payment_provider} · {order.payment_status === "paid"
                  ? t("order.status_paid", "Ödənilib")
                  : t("order.status_pending", "Gözləyir")}
              </div>
              <div className="mt-2 font-serif text-xl">{formatAZN(order.total)}</div>
              {(order.points_discount_minor || 0) > 0 && (
                <div className="mt-1 text-sm text-graphite/70" data-testid="success-points">
                  {t("order.points", "Falcon xalları")}{order.points_spent ? ` (${order.points_spent} xal)` : ""}: − {formatAZN(order.points_discount_minor)}
                </div>
              )}
              {(order.gift_card_total || 0) > 0 && (
                <div className="mt-1 text-sm text-graphite/70">{t("order.gift_card", "Hədiyyə kartı")}: − {formatAZN(order.gift_card_total)}</div>
              )}
            </div>
          )}
          <BrassRule className="my-10 max-w-sm mx-auto" />
          <div className="flex justify-center gap-3 flex-wrap">
            {finalOrderId && (
              <PillCTA as={Link} to={trackHref} variant="primary" data-testid="success-view-order">{t("order.track", "Sifarişi izlə")}</PillCTA>
            )}
            <PillCTA as={Link} to="/shop" variant="secondary" data-testid="success-continue">{t("result.continue", "Alış-verişə davam et")}</PillCTA>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

// A cancel and a hard decline used to look identical here — one reassuring
// "no payment was taken" line for both. The bank now tells us which it was
// (via ?reason=), so the shopper reads the truth: paused vs. card refused vs.
// gateway down, each with the right next step.
const CANCEL_COPY = {
  declined: {
    badge: ["result.declined_badge", "Rədd edildi"],
    title: ["result.declined_title", "Kart rədd edildi"],
    body: ["result.declined_body", "Bankınız ödənişi təsdiqləmədi. Kartın balansını, limitini və 3D-Secure təsdiqini yoxlayın — və ya başqa kartla yenidən sınayın."],
  },
  expired: {
    badge: ["result.expired_badge", "Vaxt bitdi"],
    title: ["result.expired_title", "Ödəniş vaxtı bitdi"],
    body: ["result.expired_body", "Ödəniş səhifəsi çox uzun açıq qaldı. Sifarişiniz durur — yenidən başlaya bilərsiniz."],
  },
  voided: {
    badge: ["result.voided_badge", "Ləğv edildi"],
    title: ["result.voided_title", "Ödəniş ləğv edildi"],
    body: ["result.voided_body", "Ödəniş tamamlanmadı. Sifarişiniz durur — yenidən sınaya bilərsiniz."],
  },
  unavailable: {
    badge: ["result.unavailable_badge", "Əlçatan deyil"],
    title: ["result.unavailable_title", "Ödəniş sistemi hazırda əlçatan deyil"],
    body: ["result.unavailable_body", "Kartla ödənişi indi başlada bilmədik. Bir az sonra yenidən sınayın və ya concierge ilə əlaqə saxlayın."],
  },
  notfound: {
    badge: ["result.paused", "Paused"],
    title: ["result.notfound_title", "Sifariş tapılmadı"],
    body: ["result.notfound_body", "Bu ödəniş üçün sifariş tapılmadı. Səbətinizə qayıdıb yenidən sınaya bilərsiniz."],
  },
  cancelled: {
    badge: ["result.paused", "Paused"],
    title: ["result.cancelled_title", "Sifarişiniz sizi gözləyir"],
    body: ["result.cancelled_body", "Heç bir ödəniş alınmadı. Hazır olduğunuzda davam edə bilərsiniz."],
  },
};

export function CheckoutCancelled() {
  const { t } = useI18n();
  const [sp] = useSearchParams();
  // Kapital Bank's return route redirects with ?order=; Stripe/COD use
  // ?order_id=. Read either so a KB shopper lands on a page that knows
  // which order they were paying for.
  const orderId = sp.get("order_id") || sp.get("order");
  const provider = sp.get("provider") || "";
  const reason = sp.get("reason") || "cancelled";
  const copy = CANCEL_COPY[reason] || CANCEL_COPY.cancelled;
  const navigate = useNavigate();
  const { restoreCartFromOrder } = useCart() || {};
  const [retrying, setRetrying] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState("");

  // Put the pieces back in the basket. A card payment creates the order before
  // paying, so a decline emptied the basket into an unpaid order — this rebuilds
  // it (the server cancels the dead order) so the shopper isn't left with
  // nothing. Preferred over retry when they want to change something first.
  const restoreCart = async () => {
    if (!orderId || !restoreCartFromOrder) return;
    setRestoring(true);
    setError("");
    try {
      await restoreCartFromOrder(orderId);
      navigate("/cart");
    } catch (err) {
      setError(err?.response?.data?.detail || t("result.restore_failed", "Səbət bərpa edilə bilmədi. Concierge kömək edə bilər."));
      setRestoring(false);
    }
  };

  // Placing the order empties the cart, so sending an unpaid shopper back to
  // /cart used to land them on an empty basket with no way to finish paying.
  // The order is still pending — restart *its* payment on the right provider.
  // The old button always hit the Stripe endpoint, which failed every Kapital
  // Bank order; it now re-opens the order's own gateway.
  const retryPayment = async () => {
    setRetrying(true);
    setError("");
    try {
      if (provider === "stripe") {
        const r = await api.post("/payments/stripe/checkout", {
          order_id: orderId,
          origin_url: window.location.origin,
        });
        window.location.href = r.data.url;
        return;
      }
      const r = await api.post(`/payments/${orderId}/reinitiate`, {});
      if (r.data?.action?.url) {
        window.location.href = r.data.action.url;
        return;
      }
      setError(t("result.retry_failed", "Ödəniş yenidən başladıla bilmədi. Concierge komandası kömək edə bilər."));
      setRetrying(false);
    } catch (err) {
      setError(err?.response?.data?.detail || t("result.retry_failed", "Ödəniş yenidən başladıla bilmədi. Concierge komandası kömək edə bilər."));
      setRetrying(false);
    }
  };

  const canRetry = orderId && reason !== "notfound";

  return (
    <div className="bg-ivory text-graphite">
      <Header />
      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 py-32 text-center">
        <SmallCaps>{t(copy.badge[0], copy.badge[1])}</SmallCaps>
        <h1 className="font-serif italic text-5xl mt-6">{t(copy.title[0], copy.title[1])}</h1>
        <p className="mt-6 text-graphite/75 font-light max-w-xl mx-auto">
          {t(copy.body[0], copy.body[1])}
        </p>
        {error && <p className="mt-6 text-burgundy" role="alert">{error}</p>}
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          {canRetry ? (
            <>
              <PillCTA variant="primary" onClick={restoreCart} disabled={restoring || retrying} data-testid="restore-cart">
                {restoring ? t("result.restoring", "Səbət hazırlanır…") : t("result.restore_cart", "Məhsulları səbətə qaytar")}
              </PillCTA>
              <PillCTA variant="secondary" onClick={retryPayment} disabled={retrying || restoring} data-testid="retry-payment">
                {retrying ? t("result.redirecting", "Yönləndirilir…") : t("result.retry", "Ödənişi yenidən sına")}
              </PillCTA>
            </>
          ) : (
            <PillCTA as={Link} to="/cart" variant="primary">{t("result.back_to_cart", "Səbətə qayıt")}</PillCTA>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
